import { formatDateTime } from "@/lib/format";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { glopicApi } from "@/lib/glopic-api";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import ConfirmModal from "@/components/base/ConfirmModal";
import Pagination from "@/components/base/Pagination";
import TableSortHeader from "@/components/base/TableSortHeader";
import UserIdentityCell from "@/components/base/UserIdentityCell";

interface AiUserRow {
  user_id: string;
  user_type: "consumer" | "merchant";
  nickname: string | null;
  avatar: string | null;
  phone: string | null;
  email: string | null;
  contact: string | null;
  phone_last4: string | null;
  email_masked: string | null;
  country_code: string | null;
  device_brand: string | null;
  login_type: string | null;
  points: number;
  is_vip: boolean;
  vip_expire_time: string | null;
  status: string;
  last_login_at: string | null;
  created_at: string;
  total_generations: number;
}

interface UserListSummary {
  filtered_total: number;
  current_page_count: number;
  today_new_users: number;
  last_7d_new_users: number;
  timezone: string;
}

interface VipTarget {
  userId: string;
  nickname: string;
  isVip: boolean;
  vipExpireTime: string | null;
}

interface PointsTarget {
  userId: string;
  nickname: string;
  currentPoints: number;
}

const statusLabels: Record<string, { label: string; cls: string }> = {
  active:    { label: "正常", cls: "bg-emerald-500/10 text-emerald-500" },
  suspended: { label: "冻结", cls: "bg-amber-500/10 text-amber-500" },
  banned:    { label: "封禁", cls: "bg-red-500/10 text-red-500" },
};

const userTypeLabels = {
  consumer: { label: "C端用户", cls: "bg-blue-500/10 text-blue-600" },
  merchant: { label: "商家用户", cls: "bg-violet-500/10 text-violet-600" },
} as const;

type TimeField = "created_at" | "last_login_at";
type DateRangePreset = "today" | "yesterday" | "7d" | "30d" | "month";

const countryOptions = [
  { value: "CN", label: "中国" },
  { value: "US", label: "美国" },
  { value: "JP", label: "日本" },
  { value: "KR", label: "韩国" },
  { value: "HK", label: "香港" },
  { value: "TW", label: "台湾" },
  { value: "SG", label: "新加坡" },
];

const deviceOptions = [
  { value: "iphone", label: "iPhone" },
  { value: "ipad", label: "iPad" },
  { value: "mac", label: "Mac" },
  { value: "unknown", label: "未知" },
];

const inputClass =
  "h-9 rounded-lg border border-surface-border bg-white px-3 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-brand/40";

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getPresetRange(preset: DateRangePreset): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now);
  const to = new Date(now);

  if (preset === "yesterday") {
    from.setDate(now.getDate() - 1);
    to.setDate(now.getDate() - 1);
  } else if (preset === "7d") {
    from.setDate(now.getDate() - 6);
  } else if (preset === "30d") {
    from.setDate(now.getDate() - 29);
  } else if (preset === "month") {
    from.setDate(1);
  }

  return { from: toDateInputValue(from), to: toDateInputValue(to) };
}

export default function AiUsersPage() {
  const { canAccess } = useAdminAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<AiUserRow[]>([]);
  const [loading, setLoading] = useState(true);

  // 搜索 & 筛选
  const [keyword, setKeyword] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState<"" | "consumer" | "merchant">("");
  const [statusFilter, setStatusFilter] = useState("");
  const [vipFilter, setVipFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");
  const [timeField, setTimeField] = useState<TimeField>("created_at");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // 排序 & 分页
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<UserListSummary | null>(null);
  const [pageSize, setPageSize] = useState(20);

  // 封禁弹窗
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{ userId: string; action: "ban" | "unban"; nickname: string } | null>(null);
  const [statusReason, setStatusReason] = useState("");

  // VIP 授权弹窗
  const [vipModalOpen, setVipModalOpen] = useState(false);
  const [vipTarget, setVipTarget] = useState<VipTarget | null>(null);
  const [vipDuration, setVipDuration] = useState<"monthly" | "yearly">("monthly");
  const [vipReason, setVipReason] = useState("");
  const [vipLoading, setVipLoading] = useState(false);

  // 积分充值弹窗
  const [pointsModalOpen, setPointsModalOpen] = useState(false);
  const [pointsTarget, setPointsTarget] = useState<PointsTarget | null>(null);
  const [pointsDelta, setPointsDelta] = useState("");
  const [pointsReason, setPointsReason] = useState("");
  const [pointsLoading, setPointsLoading] = useState(false);

  const handleSort = useCallback((field: string, dir: "asc" | "desc") => {
    setSortField(field || "created_at");
    setSortDir(dir);
    setPage(1);
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page,
        page_size: pageSize,
        sort_field: sortField,
        sort_dir: sortDir,
        timezone_name: "Asia/Shanghai",
      };
      if (keyword) params.keyword = keyword;
      if (userTypeFilter) params.user_type = userTypeFilter;
      if (statusFilter) params.status = statusFilter;
      if (vipFilter) params.vip_status = vipFilter;
      if (countryFilter) params.country_code = countryFilter;
      if (deviceFilter) params.device_type = deviceFilter;
      if (startDate || endDate) params.time_field = timeField;
      if (startDate) params.start_time = `${startDate}T00:00:00`;
      if (endDate) params.end_time = `${endDate}T23:59:59`;

      const response = await glopicApi.get("/users", { params });
      const data = response.data.data;
      setUsers(data.items ?? []);
      setTotal(data.total ?? 0);
      setSummary(data.summary ?? null);
    } catch (err) {
      console.error("Fetch AI users error:", err);
    } finally {
      setLoading(false);
    }
  }, [keyword, userTypeFilter, statusFilter, vipFilter, countryFilter, deviceFilter, timeField, startDate, endDate, sortField, sortDir, page, pageSize]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const resetFilters = () => {
    setKeyword(""); setUserTypeFilter(""); setStatusFilter("");
    setVipFilter(""); setCountryFilter(""); setDeviceFilter("");
    setTimeField("created_at"); setStartDate(""); setEndDate("");
    setSortField("created_at"); setSortDir("desc");
    setPage(1);
  };

  const applyDatePreset = (preset: DateRangePreset) => {
    const range = getPresetRange(preset);
    setStartDate(range.from);
    setEndDate(range.to);
    setPage(1);
  };

  // ── 封禁 / 解禁 ──────────────────────────────────────────────────────────
  const promptBan = (u: AiUserRow) => {
    const reason = window.prompt("请输入封禁原因（将写入不可变审计）")?.trim();
    if (!reason) return;
    setStatusReason(reason);
    setConfirmTarget({ userId: u.user_id, action: "ban", nickname: u.nickname || u.user_id });
    setConfirmOpen(true);
  };

  const promptUnban = (u: AiUserRow) => {
    const reason = window.prompt("请输入解禁原因（将写入不可变审计）")?.trim();
    if (!reason) return;
    setStatusReason(reason);
    setConfirmTarget({ userId: u.user_id, action: "unban", nickname: u.nickname || u.user_id });
    setConfirmOpen(true);
  };

  const executeToggleStatus = async () => {
    if (!confirmTarget) return;
    const newStatus = confirmTarget.action === "ban" ? "banned" : "active";
    try {
      await glopicApi.patch(`/users/${confirmTarget.userId}/status`, {
        status: newStatus,
        reason: statusReason,
      });
      fetchUsers();
    } catch (err) {
      console.error("Toggle status error:", err);
    }
    setConfirmOpen(false);
    setConfirmTarget(null);
    setStatusReason("");
  };

  // ── VIP 授权 ──────────────────────────────────────────────────────────────
  const openVipModal = (u: AiUserRow) => {
    setVipTarget({ userId: u.user_id, nickname: u.nickname || u.user_id, isVip: u.is_vip, vipExpireTime: u.vip_expire_time });
    setVipDuration("monthly");
    setVipReason("");
    setVipModalOpen(true);
  };

  const executeGrantVip = async () => {
    if (!vipTarget) return;
    const duration_days = vipDuration === "monthly" ? 30 : 365;
    setVipLoading(true);
    try {
      await glopicApi.post(`/users/${vipTarget.userId}/grant-vip`, {
        duration_days,
        reason: vipReason.trim(),
      });
      fetchUsers();
      setVipModalOpen(false);
    } catch (err) {
      console.error("Grant VIP error:", err);
    } finally {
      setVipLoading(false);
    }
  };

  // ── 积分充值 ──────────────────────────────────────────────────────────────
  const openPointsModal = (u: AiUserRow) => {
    setPointsTarget({ userId: u.user_id, nickname: u.nickname || u.user_id, currentPoints: u.points });
    setPointsDelta("");
    setPointsReason("");
    setPointsModalOpen(true);
  };

  const executeAdjustPoints = async () => {
    if (!pointsTarget) return;
    const delta = parseInt(pointsDelta, 10);
    if (!delta || !pointsReason.trim()) return;
    setPointsLoading(true);
    try {
      await glopicApi.post(`/users/${pointsTarget.userId}/adjust-points`, { delta, reason: pointsReason.trim() });
      fetchUsers();
      setPointsModalOpen(false);
    } catch (err) {
      console.error("Adjust points error:", err);
    } finally {
      setPointsLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);
  const canModify = canAccess("ai_users");

  const btnBase = "h-7 px-2.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap";
  const activeFilters = [
    keyword && { key: "keyword", label: `关键词：${keyword}` },
    userTypeFilter && { key: "user_type", label: `用户类型：${userTypeLabels[userTypeFilter].label}` },
    statusFilter && { key: "status", label: `状态：${statusLabels[statusFilter]?.label ?? statusFilter}` },
    vipFilter && { key: "vip", label: `VIP：${vipFilter === "vip" ? "VIP" : "非VIP"}` },
    countryFilter && { key: "country", label: `国家：${countryFilter}` },
    deviceFilter && { key: "device", label: `设备：${deviceOptions.find((item) => item.value === deviceFilter)?.label ?? deviceFilter}` },
    (startDate || endDate) && {
      key: "time",
      label: `${timeField === "created_at" ? "创建时间" : "最后登录"}：${startDate || "不限"} ~ ${endDate || "不限"}`,
    },
  ].filter(Boolean) as { key: string; label: string }[];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">用户列表</h2>
        <span className="text-text-muted text-sm">共 {total} 位用户</span>
      </div>

      {/* ── 搜索 & 筛选 ── */}
      <div className="bg-white rounded-xl border border-surface-border p-4 space-y-3">
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(260px,1.4fr)_repeat(6,minmax(120px,0.7fr))_auto] gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
              placeholder="搜索用户ID、昵称、手机号尾号、邮箱"
              className={`${inputClass} w-full pl-9 placeholder:text-text-muted`}
            />
          </div>
          <select
            value={userTypeFilter}
            onChange={(e) => { setUserTypeFilter(e.target.value as "" | "consumer" | "merchant"); setPage(1); }}
            className={inputClass}
          >
            <option value="">全部用户类型</option>
            <option value="consumer">C端用户</option>
            <option value="merchant">商家用户</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className={inputClass}
          >
            <option value="">全部状态</option>
            <option value="active">正常</option>
            <option value="suspended">冻结</option>
            <option value="banned">封禁</option>
          </select>
          <select
            value={vipFilter}
            onChange={(e) => { setVipFilter(e.target.value); setPage(1); }}
            className={inputClass}
          >
            <option value="">全部VIP</option>
            <option value="vip">VIP</option>
            <option value="non_vip">非VIP</option>
          </select>
          <select
            value={countryFilter}
            onChange={(e) => { setCountryFilter(e.target.value); setPage(1); }}
            className={inputClass}
          >
            <option value="">全部国家</option>
            {countryOptions.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <select
            value={deviceFilter}
            onChange={(e) => { setDeviceFilter(e.target.value); setPage(1); }}
            className={inputClass}
          >
            <option value="">全部设备</option>
            {deviceOptions.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <select
            value={sortField}
            onChange={(e) => { setSortField(e.target.value); setPage(1); }}
            className={inputClass}
          >
            <option value="created_at">创建时间排序</option>
            <option value="last_login_at">最后登录排序</option>
            <option value="points">积分排序</option>
          </select>
          <button
            onClick={resetFilters}
            className="h-9 px-4 rounded-lg border border-surface-border text-sm text-text-secondary hover:bg-surface transition-colors cursor-pointer whitespace-nowrap"
          >
            重置
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-surface/60 px-3 py-2">
          <select
            value={timeField}
            onChange={(e) => { setTimeField(e.target.value as TimeField); setPage(1); }}
            className="h-8 rounded-md border border-surface-border bg-white px-2 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-brand/40"
          >
            <option value="created_at">创建时间</option>
            <option value="last_login_at">最后登录</option>
          </select>
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            className="h-8 rounded-md border border-surface-border bg-white px-2 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-brand/40"
          />
          <span className="text-xs text-text-muted">至</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            className="h-8 rounded-md border border-surface-border bg-white px-2 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-brand/40"
          />
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { key: "today", label: "今天" },
              { key: "yesterday", label: "昨天" },
              { key: "7d", label: "近7天" },
              { key: "30d", label: "近30天" },
              { key: "month", label: "本月" },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => applyDatePreset(item.key as DateRangePreset)}
                className="h-8 px-2.5 rounded-md border border-transparent text-xs text-text-secondary hover:border-brand/20 hover:bg-white hover:text-brand transition-colors cursor-pointer"
              >
                {item.label}
              </button>
            ))}
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(""); setEndDate(""); setPage(1); }}
                className="h-8 px-2.5 rounded-md text-xs text-text-muted hover:bg-white hover:text-text-primary transition-colors cursor-pointer"
              >
                清空时间
              </button>
            )}
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-text-muted">已筛选</span>
            {activeFilters.map((item) => (
              <span
                key={item.key}
                className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1 text-xs text-brand"
              >
                {item.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── 表格 ── */}
      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">用户</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">用户类型</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">手机号/邮箱</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">国家</th>
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">设备</th>
                <TableSortHeader label="积分" field="points" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">VIP</th>
                <TableSortHeader label="注册时间" field="created_at" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <TableSortHeader label="最后登录" field="last_login_at" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <th className="text-left px-4 py-3.5 text-text-secondary font-medium text-xs">状态</th>
                <th className="text-right px-4 py-3.5 text-text-secondary font-medium text-xs">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
                    加载中...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-user-search-line text-3xl mb-2 block" />
                    暂无数据
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const statusCfg = statusLabels[u.status] ?? statusLabels.active;
                  const isActive = u.status === "active";
                  return (
                    <tr key={u.user_id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                      <td className="px-4 py-3.5">
                        <UserIdentityCell nickname={u.nickname} userId={u.user_id} avatar={u.avatar} />
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${userTypeLabels[u.user_type].cls}`}>
                          {userTypeLabels[u.user_type].label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="font-mono text-xs text-text-primary">
                          {u.phone ?? u.email ?? u.contact ?? <span className="text-text-muted">—</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-text-primary">
                        {u.country_code ?? <span className="text-text-muted">—</span>}
                      </td>
                      <td className="px-4 py-3.5 text-xs text-text-primary">
                        {u.device_brand ?? <span className="text-text-muted">—</span>}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-sm text-text-primary">{u.points}</td>
                      <td className="px-4 py-3.5">
                        {u.is_vip ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-500/10 text-amber-500 w-fit">
                              <i className="ri-vip-crown-line" />VIP
                            </span>
                            {u.vip_expire_time && (
                              <span className="text-[10px] text-text-muted">{formatDateTime(u.vip_expire_time)}</span>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-surface text-text-muted">普通</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-xs text-text-muted whitespace-nowrap">
                        {formatDateTime(u.created_at)}
                      </td>
                      <td className="px-4 py-3.5 text-xs text-text-muted whitespace-nowrap">
                        {u.last_login_at ? formatDateTime(u.last_login_at) : <span className="text-text-muted">—</span>}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-medium ${statusCfg.cls}`}>
                          {statusCfg.label}
                        </span>
                      </td>

                      {/* 操作 */}
                      <td className="px-4 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          <button
                            onClick={() => navigate(`/ai/users/${u.user_id}`)}
                            className={`${btnBase} border border-surface-border text-text-secondary hover:bg-surface hover:text-brand`}
                          >
                            详情
                          </button>

                          {canModify && (
                            <>
                              <button
                                onClick={() => openPointsModal(u)}
                                className={`${btnBase} border border-surface-border text-text-secondary hover:bg-surface hover:text-brand`}
                              >
                                充值积分
                              </button>

                              <button
                                onClick={() => openVipModal(u)}
                                className={`${btnBase} border border-amber-200 text-amber-600 hover:bg-amber-50`}
                              >
                                授权VIP
                              </button>

                              {isActive && (
                                <button
                                  onClick={() => promptBan(u)}
                                  className={`${btnBase} border border-red-200 text-red-500 hover:bg-red-50`}
                                >
                                  封禁
                                </button>
                              )}

                              {!isActive && (
                                <button
                                  onClick={() => promptUnban(u)}
                                  className={`${btnBase} border border-emerald-200 text-emerald-600 hover:bg-emerald-50`}
                                >
                                  解禁
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-px border-t border-surface-border bg-surface-border md:grid-cols-4">
          {[
            { label: "当前筛选总数", value: summary?.filtered_total ?? total },
            { label: "当前页展示", value: summary?.current_page_count ?? users.length },
            { label: "今日新增", value: summary?.today_new_users ?? 0 },
            { label: "近 7 日新增", value: summary?.last_7d_new_users ?? 0 },
          ].map((item) => (
            <div key={item.label} className="bg-white px-4 py-3">
              <p className="text-xs text-text-muted">{item.label}</p>
              <p className="mt-1 font-mono text-lg font-semibold text-text-primary">
                {Number(item.value || 0).toLocaleString("zh-CN")}
              </p>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </div>

      {/* ── 封禁/解禁确认弹窗 ── */}
      <ConfirmModal
        open={confirmOpen}
        title={confirmTarget?.action === "ban" ? "封禁用户" : "解禁用户"}
        description={
          confirmTarget?.action === "ban"
            ? `确认封禁用户 "${confirmTarget.nickname}"？封禁后该用户将无法使用 AI 生图功能。`
            : `确认解禁用户 "${confirmTarget?.nickname}"？解禁后该用户将恢复正常使用。`
        }
        confirmText={confirmTarget?.action === "ban" ? "确认封禁" : "确认解禁"}
        confirmDanger={confirmTarget?.action === "ban"}
        onConfirm={executeToggleStatus}
        onCancel={() => { setConfirmOpen(false); setConfirmTarget(null); setStatusReason(""); }}
      />

      {/* ── VIP 授权弹窗 ── */}
      {vipModalOpen && vipTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-text-primary">VIP 授权</h3>
            <p className="text-sm text-text-secondary">
              用户：<span className="font-medium text-text-primary">{vipTarget.nickname}</span>
            </p>
            {vipTarget.isVip && vipTarget.vipExpireTime && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                当前已是 VIP，到期：{formatDateTime(vipTarget.vipExpireTime)}，授权将在此基础上追加天数。
              </p>
            )}
            <div className="space-y-2">
              <p className="text-xs text-text-secondary">选择订阅类型</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setVipDuration("monthly")}
                  className={`py-3 rounded-xl border-2 text-sm font-medium transition-colors cursor-pointer ${
                    vipDuration === "monthly"
                      ? "border-amber-400 bg-amber-50 text-amber-600"
                      : "border-surface-border text-text-secondary hover:border-amber-200"
                  }`}
                >
                  <div className="font-semibold">月度订阅</div>
                  <div className="text-xs mt-0.5 opacity-70">30 天</div>
                </button>
                <button
                  onClick={() => setVipDuration("yearly")}
                  className={`py-3 rounded-xl border-2 text-sm font-medium transition-colors cursor-pointer ${
                    vipDuration === "yearly"
                      ? "border-amber-400 bg-amber-50 text-amber-600"
                      : "border-surface-border text-text-secondary hover:border-amber-200"
                  }`}
                >
                  <div className="font-semibold">年度订阅</div>
                  <div className="text-xs mt-0.5 opacity-70">365 天</div>
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-text-secondary">授权原因</label>
              <input
                type="text"
                value={vipReason}
                onChange={(event) => setVipReason(event.target.value)}
                placeholder="请输入授权原因（必填）"
                className="w-full h-9 px-3 rounded-lg border border-surface-border text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-brand/40"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setVipModalOpen(false)}
                className="flex-1 h-9 rounded-lg border border-surface-border text-sm text-text-secondary hover:bg-surface transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={executeGrantVip}
                disabled={vipLoading || !vipReason.trim()}
                className="flex-1 h-9 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer disabled:opacity-60"
              >
                {vipLoading ? "授权中..." : "确认授权"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 积分充值弹窗 ── */}
      {pointsModalOpen && pointsTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-text-primary">积分充值</h3>
            <p className="text-sm text-text-secondary">
              用户：<span className="font-medium text-text-primary">{pointsTarget.nickname}</span>
              <span className="ml-2 text-text-muted">当前积分：{pointsTarget.currentPoints}</span>
            </p>
            <div className="space-y-1">
              <label className="text-xs text-text-secondary">充值积分数（正数充值，负数扣除）</label>
              <input
                type="number"
                value={pointsDelta}
                onChange={(e) => setPointsDelta(e.target.value)}
                placeholder="例：100 或 -50"
                className="w-full h-9 px-3 rounded-lg border border-surface-border text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-brand/40"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-text-secondary">备注原因</label>
              <input
                type="text"
                value={pointsReason}
                onChange={(e) => setPointsReason(e.target.value)}
                placeholder="例：活动奖励、补偿等"
                className="w-full h-9 px-3 rounded-lg border border-surface-border text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-brand/40"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setPointsModalOpen(false)}
                className="flex-1 h-9 rounded-lg border border-surface-border text-sm text-text-secondary hover:bg-surface transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={executeAdjustPoints}
                disabled={pointsLoading || !pointsDelta || !pointsReason.trim()}
                className="flex-1 h-9 rounded-lg bg-brand text-white text-sm font-medium hover:bg-brand/90 transition-colors cursor-pointer disabled:opacity-60"
              >
                {pointsLoading ? "提交中..." : "确认充值"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
